import type { County, Projection } from '@hotlist/shared';
import { project, unproject } from '@hotlist/shared';

/**
 * Pan and zoom over the projected ground plane.
 *
 * The viewport works entirely in projected units, which are meters with the
 * north-south axis already foreshortened. Nothing outside this file needs to
 * know the difference between a world meter and a projected one, and nothing
 * inside it needs to know about the tilt.
 */
export class Viewport {
  /** Center of the view, in projected units. */
  centerX = 0;
  centerY = 0;
  /** Screen pixels per projected unit. */
  scale = 0.02;

  width = 1;
  height = 1;

  minScale = 0.002;
  maxScale = 4;

  private readonly projection: Projection;

  constructor(projection: Projection) {
    this.projection = projection;
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  /** Frame the whole county with a margin. */
  fit(county: County, margin = 0.06): void {
    const corners = [
      project(this.projection, county.bounds.minX, county.bounds.minY),
      project(this.projection, county.bounds.maxX, county.bounds.minY),
      project(this.projection, county.bounds.minX, county.bounds.maxY),
      project(this.projection, county.bounds.maxX, county.bounds.maxY),
    ];
    const minX = Math.min(...corners.map((c) => c.sx));
    const maxX = Math.max(...corners.map((c) => c.sx));
    const minY = Math.min(...corners.map((c) => c.sy));
    const maxY = Math.max(...corners.map((c) => c.sy));

    this.centerX = (minX + maxX) / 2;
    this.centerY = (minY + maxY) / 2;
    const fitScale = Math.min(this.width / (maxX - minX), this.height / (maxY - minY));
    this.scale = fitScale * (1 - margin);
    /**
     * You cannot zoom out past the whole county.
     *
     * The floor was 60% of the fitted scale, which lets the county shrink to
     * about a third of the space it fits in -- so four fifths of the screen is
     * black and the map is a postage stamp in the middle of it. There is
     * nothing out there to look at: the county is the world, and the edge of it
     * is the edge.
     */
    this.minScale = this.scale;
  }

  toScreenX(sx: number): number {
    return (sx - this.centerX) * this.scale + this.width / 2;
  }

  toScreenY(sy: number): number {
    return (sy - this.centerY) * this.scale + this.height / 2;
  }

  fromScreen(px: number, py: number): { sx: number; sy: number } {
    return {
      sx: (px - this.width / 2) / this.scale + this.centerX,
      sy: (py - this.height / 2) / this.scale + this.centerY,
    };
  }

  /** Screen point to a place on the ground, in world meters. */
  toWorld(px: number, py: number): { x: number; y: number } {
    const { sx, sy } = this.fromScreen(px, py);
    return unproject(this.projection, sx, sy);
  }

  panByPixels(dx: number, dy: number): void {
    this.centerX -= dx / this.scale;
    this.centerY -= dy / this.scale;
  }

  /** Zoom about a screen point, so the ground under the cursor stays put. */
  zoomAt(px: number, py: number, factor: number): void {
    const before = this.fromScreen(px, py);
    this.scale = clamp(this.scale * factor, this.minScale, this.maxScale);
    const after = this.fromScreen(px, py);
    this.centerX += before.sx - after.sx;
    this.centerY += before.sy - after.sy;
  }

  centerOnWorld(x: number, y: number): void {
    const p = project(this.projection, x, y);
    this.centerX = p.sx;
    this.centerY = p.sy;
  }

  /** Keep the view over the county rather than out in empty space. */
  clampTo(county: County, slack = 0.35): void {
    const southWest = project(this.projection, county.bounds.minX, county.bounds.minY);
    const northEast = project(this.projection, county.bounds.maxX, county.bounds.maxY);
    const minX = Math.min(southWest.sx, northEast.sx);
    const maxX = Math.max(southWest.sx, northEast.sx);
    const minY = Math.min(southWest.sy, northEast.sy);
    const maxY = Math.max(southWest.sy, northEast.sy);
    const padX = (maxX - minX) * slack;
    const padY = (maxY - minY) * slack;
    this.centerX = clamp(this.centerX, minX - padX, maxX + padX);
    this.centerY = clamp(this.centerY, minY - padY, maxY + padY);
  }

  /** Visible ground rectangle in projected units, with a pixel margin. */
  visibleBounds(marginPixels = 64): {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } {
    const halfWidth = (this.width / 2 + marginPixels) / this.scale;
    const halfHeight = (this.height / 2 + marginPixels) / this.scale;
    return {
      minX: this.centerX - halfWidth,
      minY: this.centerY - halfHeight,
      maxX: this.centerX + halfWidth,
      maxY: this.centerY + halfHeight,
    };
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
